import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { DemandCarliChat } from './DemandCarliChat';
import { DemandPlatformConnections } from './DemandPlatformConnections';
import { DemandOpportunity } from './DemandOpportunity';
import { DemandAdStudio } from './DemandAdStudio';
import { DemandCampaigns } from './DemandCampaigns';
import { DemandSocial } from './DemandSocial';
import { demandMetrics } from '../data/demandData';
type DemandScope = {
  label: string;
  detail: string;
};
const tabs = ['Overview', 'Opportunity', 'Ad Studio', 'Campaigns', 'Social', 'Connections'] as const;
type DemandTab = (typeof tabs)[number];
function getDemandScope(selectedOwner: string): DemandScope {
  if (selectedOwner === 'Eddy') return {
    label: 'Eddy',
    detail: 'Eddy inventory and approved channel context'
  };
  if (selectedOwner === '1 People') return {
    label: '1 People',
    detail: '1 People inventory and approved channel context'
  };
  if (selectedOwner) return {
    label: selectedOwner,
    detail: 'Selected brand inventory and approved channel context'
  };
  return {
    label: 'All available brands',
    detail: 'Live inventory, campaign, and channel context across approved brands'
  };
}
export function DemandDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const scope = getDemandScope(selectedOwner);
  const [tab, setTab] = useState<DemandTab>('Overview');
  const [workspaceOpen, setWorkspaceOpen] = useState(true);
  return <div className="mx-auto max-w-[1500px] px-5 py-6 sm:px-8 lg:py-8" aria-labelledby="demand-heading" data-id="element-768" data-mp="3ijhti70g6bt7">
      <h1 id="demand-heading" className="sr-only" data-id="element-769" data-mp="3dolsihd8ge3y">Carli for Demand</h1>

      <div className="border-b border-ink/10" role="tablist" aria-label="Demand sections" data-id="element-770" data-mp="38s51nfenfln6">
        <div className="-mb-px flex gap-1 overflow-x-auto" data-id="element-771" data-mp="34rbckiph00sp">
          {tabs.map(option => {
          const active = tab === option;
          return <button key={option} type="button" role="tab" aria-selected={active} onClick={() => setTab(option)} className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${active ? 'border-[#BB5E40] text-[#94452E]' : 'border-transparent text-ink/55 hover:text-ink'}`} data-id="element-772" data-mp="2us43i7prqsbe">
                {option}
              </button>;
        })}
        </div>
      </div>

      <div className="mt-7" data-id="element-773" data-mp="33v810jzj4nr2">
        {tab === 'Overview' && <>
            <section aria-label="Demand performance" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-774" data-mp="38srf9jw890j0">
              {demandMetrics.map(metric => <div key={metric.label} className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-775" data-mp="2z3zq1jijwubs">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-776" data-mp="3mtkcwdhtsbqc">{metric.label}</p>
                  <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-777" data-mp="3rktzf582uyy0">{metric.value}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-ink/50" data-id="element-778" data-mp="1g4s61hz7y1je">{metric.detail}</p>
                </div>)}
            </section>

            <section aria-label="Carli collaboration workspace" className="mt-7" data-id="element-779" data-mp="1b44copnip8m0">
              <button type="button" onClick={() => setWorkspaceOpen(open => !open)} aria-expanded={workspaceOpen} className={`flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left ${workspaceOpen ? 'mb-3' : 'border border-ink/10 bg-paper shadow-sm'}`} data-id="element-780" data-mp="1pulbe18rpkf7">
                <span className="flex items-center gap-2 text-sm font-semibold" data-id="element-781" data-mp="167nfl5ycbjws">
                  <span aria-hidden="true" className="h-4 w-4 shrink-0 bg-[#BB5E40]" style={{
                WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
                maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain'
              }} data-id="element-782" data-mp="125k44hbo8fbi" />
                  Carli workspace
                  <span className="hidden text-xs font-normal text-ink/50 sm:inline" data-id="element-783" data-mp="3rthrzaslmb43">· Conversations, campaign rooms, and team channels</span>
                </span>
                {workspaceOpen ? <ChevronDownIcon className="h-4 w-4 text-ink/45" data-id="element-784" data-mp="4hyy1m75dutx" /> : <ChevronRightIcon className="h-4 w-4 text-ink/45" data-id="element-785" data-mp="9fqy53hy4n97" />}
              </button>
              {workspaceOpen && <DemandCarliChat scopeLabel={scope.label} scopeDetail={scope.detail} data-id="element-786" data-mp="ef3mxtsdo4ef" />}
            </section>
          </>}

        {tab === 'Opportunity' && <DemandOpportunity selectedOwner={selectedOwner} data-id="element-787" data-mp="je6h2j6tkq7m" />}
        {tab === 'Ad Studio' && <DemandAdStudio data-id="element-788" data-mp="23wqpty9g6leh" />}
        {tab === 'Campaigns' && <DemandCampaigns selectedOwner={selectedOwner} data-id="element-789" data-mp="1yww2u5ssmfyp" />}
        {tab === 'Social' && <DemandSocial data-id="element-790" data-mp="1yyywr0x2gt8p" />}
        {tab === 'Connections' && <DemandPlatformConnections data-id="element-791" data-mp="1tyugnc02m0j0" />}
      </div>
    </div>;
}